/**
 * Normalizacja rankingu do rozkładu N(50,17). Nie zmienia scoringu CRM.
 * Dla remisów przydziela percentyl środkowy — nie rozstrzyga remisów losowo.
 * Populacja odniesienia: cała pobrana lista potencjalnych klientów, bez własnych
 * klientów, blokad i nowych otwarć. Nigdy nie normalizujemy po filtrach UI.
 * Nowe otwarcia to ODRĘBNA kolejka ekspresowa: nie dostają wyniku 100. 
 */
function inverseNormal(p) {
  if (p <= 0 || p >= 1) throw new RangeError('p must lie strictly between 0 and 1');
  // Algorytm aproksymacji funkcji odwrotnej Φ (Peter J. Acklam).
  const a=[-3.969683028665376e1,2.209460984245205e2,-2.759285104469687e2,1.38357751867269e2,-3.066479806614716e1,2.506628277459239];
  const b=[-5.447609879822406e1,1.615858368580409e2,-1.556989798598866e2,6.680131188771972e1,-1.328068155288572e1];
  const c=[-7.784894002430293e-3,-3.223964580411365e-1,-2.400758277161838,-2.549732539343734,4.374664141464968,2.938163982698783];
  const d=[7.784695709041462e-3,3.224671290700398e-1,2.445134137142996,3.754408661907416];
  const qLow=0.02425;
  if(p<qLow){const q=Math.sqrt(-2*Math.log(p));return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5])/((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);}
  if(p>1-qLow){const q=Math.sqrt(-2*Math.log(1-p));return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5])/((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);}
  const q=p-0.5,r=q*q;
  return (((((a[0]*r+a[1])*r+a[2])*r+a[3])*r+a[4])*r+a[5])*q / (((((b[0]*r+b[1])*r+b[2])*r+b[3])*r+b[4])*r+1);
}

export function applyGaussianPriority(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const result=items.map(item=>({...item,contact_priority:null,priority_lane:'market'}));
  const candidates=[];
  for(let i=0;i<result.length;i++){
    const row=result[i];
    if(row.is_own_customer){row.contact_priority=0;row.priority_lane='customer';continue;}
    if(row.startup_priority?.priority){row.contact_priority=null;row.priority_lane='express';continue;}
    const raw=Number(row.priority_raw_score);
    if(row.priority_raw_score==null || !Number.isFinite(raw)){row.priority_lane='unranked';continue;}
    candidates.push({index:i,raw});
  }
  candidates.sort((a,b)=>a.raw-b.raw || a.index-b.index);
  const n=candidates.length;
  for(let i=0;i<n;){
    let j=i+1;
    while(j<n && Math.abs(candidates[j].raw-candidates[i].raw)<0.000001)j++;
    const midrank=(i+j-1)/2;
    const probability=(midrank+0.5)/n;
    const normalized=n===1?50:Math.max(5,Math.min(95,Math.round(50+17*inverseNormal(probability))));
    for(let k=i;k<j;k++) result[candidates[k].index].contact_priority=normalized;
    i=j;
  }
  return result;
}

export function contactPriorityLabel(row){
  if(row.is_own_customer)return 'Nasz klient';
  if(row.priority_lane==='express'||row.startup_priority?.priority)return 'Pilny kontakt · poza skalą';
  const v=row.contact_priority;
  if(v==null)return 'Brak podstaw / blokada';
  if(v>=90)return 'Ścisła czołówka';
  if(v>=75)return 'Wysoki priorytet';
  if(v>=60)return 'Powyżej średniej';
  if(v>=40)return 'Średni priorytet';
  return 'Niski priorytet';
}

/**
 * Priorytet dla osobnej kolejki otwarć. To porządek operacyjny, nie liczba
 * dodawana do Gaussa. Potwierdzone najbliższe otwarcia -> świeże otwarcia
 * -> nowe wpisy do zweryfikowania.
 */
export function compareExpressPriority(a, b) {
  const group = row => {
    const status = row?.startup_priority?.status;
    if (status === 'preopening') return 3;
    if (status === 'recent_opening') return 2;
    if (status === 'new_registry_entry') return 1;
    return 0;
  };
  const ga = group(a), gb = group(b);
  if (ga !== gb) return gb - ga;
  const dateOf = row => {
    const value = row?.startup_priority?.opening_date || row?.since_date;
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
    return value.slice(0, 10);
  };
  const da = dateOf(a), db = dateOf(b);
  if (da && db && da !== db) {
    // Przed otwarciem: najbliższy termin pierwszy. Pozostałe: najświeższe pierwsze.
    return ga === 3 ? da.localeCompare(db) : db.localeCompare(da);
  }
  if (da !== db) return da === null ? 1 : -1;
  return String(a.id || '').localeCompare(String(b.id || ''));
}

export function expressPriorityLabel(row) {
  const status=row?.startup_priority?.status;
  const opening=row?.startup_priority?.opening_date;
  if(status==='preopening')return opening?`Planowane otwarcie: ${opening}`:'Planowane otwarcie';
  if(status==='recent_opening')return opening?`Otwarte: ${opening}`:'Niedawno otwarte';
  if(status==='new_registry_entry')return 'Nowy wpis — zweryfikuj otwarcie';
  return 'Pilny kontakt';
}
